import React, {useState, useCallback} from 'react';
import {
  View,
  Image,
  Text,
  StyleSheet,
  TouchableOpacity,
  Linking,
  ActivityIndicator,
  Alert,
  useWindowDimensions,
} from 'react-native';

const BANNER_ASPECT_RATIO = 16 / 9;
const DESKTOP_MAX_BANNER_HEIGHT = 430;
import ProductsService from '../../../../../../services/ProductsService/ProductsService';
import {Colors} from '../../../../../../constants/Colors';
import {useNavigation} from '@react-navigation/native';
import {AppRoutes} from '../../../../../../navigation/Routes.types';
import {useCatalogStore} from '../../../../../../store/CatalogStore';
import {useUserStore} from '../../../../../../store/UserStore';
import { getAnalytics, logEvent } from '@react-native-firebase/analytics';
import {useIsDesktop} from '../../../../../../hooks/useIsDesktop';

interface BannerProps {
  id ?: number;
  imageUrl?: string;
  title?: string;
  text_color?: string;
  background_color?: string;
  variation?: string;
  linked?: string;
  link: string;
  product_sku: string;
  brand_name?: String;
  category_name?: String;
  product?: Object;
}

// O admin chegou a gravar null do JS como o texto "null"; tratado como SKU, a
// busca por "null" casava com produtos de SUBMENU "NULL" e abria um aleatório.
const cleanLinkValue = (value?: String | null): string | undefined => {
  const text = String(value ?? '').trim();
  return text === '' || ['null', 'undefined'].includes(text.toLowerCase())
    ? undefined
    : text;
};

interface CatalogFilter {
  sku?: string;
  category?: string;
  brand?: string;
}

const Banner: React.FC<BannerProps> = ({
  id,
  imageUrl,
  title,
  text_color = '#fff',
  background_color = Colors.RedPallete.red,
  variation,
  linked,
  link,
  brand_name,
  category_name,
  product_sku,
}) => {
  const isDesktop = useIsDesktop();
  const { width } = useWindowDimensions();
  const bannerHeight = isDesktop
    ? Math.min(Math.round(width / BANNER_ASPECT_RATIO), DESKTOP_MAX_BANNER_HEIGHT)
    : 200;
  const Wrapper = linked !== 'none' ? TouchableOpacity : View;
  const backgroundColor = background_color || Colors.RedPallete.red;
  const textColor = text_color || '#fff';
  const nav = useNavigation();

  const {user} = useUserStore();

  const {catalogData} = useCatalogStore();
  const [loading, setLoading] = useState(false); // Estado de carregamento

  // Primeiro produto do catálogo DO CLIENTE que atende ao filtro, ou null.
  const firstInCatalog = async (filter: CatalogFilter) => {
    const response = await ProductsService.catalogProductsGet(
      user?.clientCode,
      1,
      1,
      filter.category,
      filter.brand,
      undefined,
      undefined,
      filter.sku,
    );
    return response?.data?.products?.[0] ?? null;
  };

  // A Home (Catálogo Inteligente) não lista mais produtos filtrados pelo
  // FilterStore — e ainda limpa os filtros ao montar. O catálogo filtrado
  // agora é a CatalogBrowseScreen, mesmo destino das pills de categoria.
  const navigateToCatalog = ({category, brand}: CatalogFilter) => {
    nav.navigate(AppRoutes.CATALOG_BROWSE, {
      filter: {
        ...(category ? {categoryNames: [category], lockedCategory: true} : {}),
        ...(brand ? {brandNames: [brand]} : {}),
        title: category ?? brand,
      },
    });
  };

  // Abre o catálogo pelo filtro mais específico que tem produto no catálogo
  // do cliente: categoria + marca, só a categoria ou só a marca.
  const openCatalogIfAvailable = async (category?: string, brand?: string) => {
    const candidates: CatalogFilter[] = [
      {category, brand},
      {category},
      {brand},
    ].filter(
      (candidate, index, all) =>
        (candidate.category || candidate.brand) &&
        all.findIndex(
          other =>
            other.category === candidate.category &&
            other.brand === candidate.brand,
        ) === index,
    );

    for (const candidate of candidates) {
      if (await firstInCatalog(candidate)) {
        navigateToCatalog(candidate);
        return true;
      }
    }
    return false;
  };

  const openInternalLink = async (
    sku?: string,
    category?: string,
    brand?: string,
  ) => {
    if (link === AppRoutes.PRODUCT_DETAILS) {
      const product = sku ? await firstInCatalog({sku}) : null;
      if (product) {
        nav.navigate(AppRoutes.PRODUCT_DETAILS, {item: product, fromBanner: true});
      } else if (!(await openCatalogIfAvailable(category, brand))) {
        Alert.alert(
          'Produto indisponível',
          'Este produto não está disponível para a sua loja no momento.',
        );
      }
      return;
    }

    // MainTabScreen com categoria/marca fora do catálogo do cliente fica na Home.
    if (!(await openCatalogIfAvailable(category, brand))) {
      nav.navigate(AppRoutes.MAIN_TAB_SCREEN);
    }
  };

 const analyticsInstance = getAnalytics();
  const onPress = async () => {

     logEvent(analyticsInstance, 'banner_click', {
      banner_id: id,
      banner_title: title,
      product_sku: product_sku,
      client_code: user?.clientCode,
    });
    if (linked === 'none') {
      return;
    }

    const sku = cleanLinkValue(product_sku);
    const category = cleanLinkValue(category_name);
    const brand = cleanLinkValue(brand_name);

    if (
      linked === 'internal' &&
      (link === AppRoutes.MAIN_TAB_SCREEN || link === AppRoutes.PRODUCT_DETAILS)
    ) {
      setLoading(true);
      try {
        await openInternalLink(sku, category, brand);
      } catch (error) {
        console.error(error);
        // Sem conseguir consultar o catálogo, segue o redirect sem a checagem.
        if (sku) {
          nav.navigate(AppRoutes.PRODUCT_DETAILS, {sku, fromBanner: true});
        } else if (category || brand) {
          navigateToCatalog({category, brand});
        } else {
          nav.navigate(AppRoutes.MAIN_TAB_SCREEN);
        }
      } finally {
        setLoading(false);
      }
    } else if (linked === 'internal') {
      nav.navigate(link);
    } else {
      Linking.openURL(link);
    }
  };

  const imageStyle = [
    styles.bannerImage,
    { height: bannerHeight, resizeMode: 'cover' as const },
  ];

  return (
    <Wrapper
      onPress={onPress}
      style={[styles.banner, !imageUrl && { backgroundColor }]}
      disabled={loading}
    >
      {loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={Colors.RedPallete.red} />
        </View>
      ) : variation === 'image_only' ? (
        <Image source={{uri: imageUrl}} style={imageStyle} />
      ) : variation === 'image_with_text' ? (
        <>
          <Image source={{uri: imageUrl}} style={imageStyle} />
          <Text style={[styles.bannerText, {color: textColor}]}>{title}</Text>
        </>
      ) : (
        <View style={{backgroundColor, width: '100%', height: bannerHeight}}>
          <Text style={[styles.bannerText, {color: textColor}]}>{title}</Text>
        </View>
      )}
    </Wrapper>
  );
};

const styles = StyleSheet.create({
  banner: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  bannerImage: {
    width: '100%',
    height: 200,
  },
  bannerText: {
    position: 'absolute',
    left: 20,
    maxWidth: '85%',
    bottom: 25,
    fontSize: 18,
    fontWeight: 'bold',
  },
  loader: {
    position: 'absolute',
  },
  loadingContainer: {
    position: 'absolute',
    top: 100,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 10,
  },
});

export default Banner;
